import { ElementRef } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined, isEqualAtDepth } from '@sama/js-core';
import {
  capitalize,
  every,
  get,
  keyBy,
  mapValues,
  pick,
  values,
  words,
} from '@sama/micro-dash';
import { flatten, isNumber } from 'lodash';
import { animationFrameScheduler, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ShapeGroupOrderState } from '../team-apis/content/content-connector';
import { EventTrackingValue } from '../team-apis/core/event-tracking';
import {
  Point2D,
  PointType,
} from '../team-apis/otterdam/answers/shape-typings';
import { Task } from '../team-apis/otterdam/task';
import {
  ShapeGroupState,
  ShapeOrGroupOrderItem,
} from './state/shape-group-state';
import { ObjectWith } from './types/misc';

export interface EventTrackingValueWithSource extends EventTrackingValue {
  source: string;
}

export function truncateToPoint2D(point: PointType) {
  return point.slice(0, 2) as Point2D;
}

export function getInputUrls(
  task: Task,
  inputName: string,
  signedUrls: ObjectWith<string[]> | undefined,
) {
  const override = getUrlParam('overrideUrls');
  if (override) {
    return [override];
  }

  const unsigned = task.data[inputName];
  if (!unsigned) {
    return [];
  }

  return get(signedUrls, [unsigned], [unsigned]);
}

export function selectionToArray(selection: object) {
  return Object.keys(selection).map((index) => +index);
}

export function objectNumberedKeys(obj: StrictRecord<number, object>) {
  return Object.keys(obj).map(Number);
}

export function arrayToSubpointSelection(asArray: number[]) {
  return mapValues(
    keyBy(asArray, (index) => index.toString()),
    (): true => true,
  );
}

export function makeDataUrl(svg: string, pointerCoordinates = '') {
  return `url('data:image/svg+xml;utf8,${svg.replace(
    /\n/g,
    '',
  )}') ${pointerCoordinates}, auto`;
}

export function observeShapeGroupOrderChanges<T extends ShapeGroupOrderState>(
  store$: Observable<T>,
) {
  return store$.pipe(
    map((state) => pick(state, 'shapes', 'groups', 'shapeAndGroupOrder')),
    filter((shapeGroupOrderState) =>
      every(values(shapeGroupOrderState), isDefined),
    ),
    distinctUntilChanged((x, y) => isEqualAtDepth(1, x, y)),
  );
}

// We don't debounce rendering in test environments in order to avoid having
// to add calls to .tick() everywhere
export function getAnimationFrameScheduler() {
  if (environment.shouldDebounceRendering) {
    return animationFrameScheduler;
  } else {
    return undefined;
  }
}

export function getUrlParam(name: string) {
  return new URLSearchParams(window.location.search).get(name) || undefined;
}

export function humanize(str: string) {
  return capitalize(words(str).join(' '));
}

export function expandShapeGroups(
  items: ShapeOrGroupOrderItem[],
  groups: StrictRecord<number, ShapeGroupState>,
): ShapeOrGroupOrderItem[] {
  return flatten(
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    (items ?? []).map((item) => {
      if (item.type === 'shape') {
        return item;
      }

      return groups[item.index]!.shapeIndexes.map(
        (index: number): ShapeOrGroupOrderItem => ({ type: 'shape', index }),
      );
    }),
  );
}

export function assertStore<T>(
  store: Store<T | undefined>,
  message: string,
): asserts store is Store<T> {
  assert(isStoreDefined(store), message);
}

export function isStoreDefined<T>(
  store: Store<T | undefined>,
): store is Store<T> {
  return isDefined(store.state());
}

export function setCssProperty(
  elementRef: ElementRef<HTMLElement> | ElementRef<SVGElement> | HTMLElement,
  property: string,
  value: string | number | null = null,
) {
  let validValue: string | null;
  if (isNumber(value)) {
    validValue = value.toString();
  } else {
    validValue = value;
  }
  const nativeElement =
    'nativeElement' in elementRef ? elementRef.nativeElement : elementRef;
  nativeElement.style.setProperty(property, validValue);
}

export function getCssProperty(
  elementRef: ElementRef<HTMLElement> | ElementRef<SVGElement> | HTMLElement,
  property: string,
) {
  const nativeElement =
    'nativeElement' in elementRef ? elementRef.nativeElement : elementRef;
  return nativeElement.style.getPropertyValue(property);
}
